import { Cell, CellKind, Grid, LevelTerrain, RoadMap } from "@highmarch/domain";
import { mulberry, noise2, smoothstep } from "./terrainNoise";
import { BridgeFit } from "./bridgeFit";
import { BridgeSpan, BridgeSpans } from "./bridgeSpans";

/**
 * Presentation-only relief for a level.
 *
 * The first version quantised the map into height levels 0/1/2 and put a vertical cliff on every step, which turned
 * the map into a wedding cake: rigid rectangular terraces on the cell grid, nothing like the reference art. The
 * ground here is instead a single CONTINUOUS height field — rolling hills and broad benches that cross cell
 * boundaries. Nothing is quantised and there are no terraces.
 *
 * Vertical drama comes from the two things the references use for it: benches at different heights joined by ramps,
 * and chasms — a gap in the ground with a cliff face all round and a plank bridge where the road crosses one.
 *
 * `heightAt` returns the world y for any point, so units, towers, effects and critters sit on the relief. The
 * simulation stays a flat grid; the one thing it knows about is which cells are `CellKind.Chasm`, because the road
 * may be routed across those.
 */
export class Terrain {
  /** How far the rolling ground rises and falls, peak to trough, in world units (a profile may override it). */
  static readonly Hills = 0.62;
  /**
   * How far below the ground a chasm's floor and a lava basin's melt sit.
   *
   * These are DEPTHS, not world heights. The land now climbs through benches a metre or more apart, so a fixed world
   * y would put a gorge on the high bench four units down and one on the low bench barely a step down. Measuring
   * from the ground above keeps every gorge the same gorge.
   */
  static readonly PitDepth = 2.3;
  static readonly LavaDepth = 1.05;
  /** The level in use (views read it through `groundAt`). */
  static current: Terrain | null = null;
  /** The bridges on the current road (bridgeSpans.ts). */
  private readonly bridges: BridgeSpans;
  /** The level's chasm cells, split by what is at the bottom: rubble, or melt. */
  readonly pits = new Set<number>();
  readonly lava = new Set<number>();
  /** Noise offsets and the flat pad around the fortress. */
  private readonly ox: number;
  private readonly oz: number;
  private readonly scale: number;
  private readonly padX: number;
  private readonly padZ: number;

  /**
   * What a level's ground is made of, chosen by level number so consecutive levels do not look like one another.
   *
   * `scale` is the noise frequency: SMALLER means bigger benches. At 0.075 a tier band came out about four units
   * wide and the levels read as corduroy — long parallel ramps with no flat ground between them. At ~0.05 a bench is
   * seven or eight units, which is a plateau you can put a fight on.
   *
   * `tiers` is how many plateau steps the land climbs through, `rise` how far apart they sit and `bench` how much of
   * a tier's width is spent on the ramp between them: a big `bench` is a hillside, a small one is a bluff. This is
   * the difference between the wedding-cake version and the reference — the tier EDGES are curved and warped and the
   * transitions are ramps, but the tops are genuinely flat and genuinely at different heights, which is where the
   * depth comes from. `chasm` is the share of a level's blocked clusters that open into a gorge.
   */
  static readonly Profiles: { name: string; hills: number; tiers: number; rise: number; bench: number; scale: number; chasm: number }[] = [
    { name: "downs", hills: 0.55, tiers: 2, rise: 1.10, bench: 0.30, scale: 0.052, chasm: 0.25 },
    { name: "benches", hills: 0.45, tiers: 2, rise: 1.45, bench: 0.22, scale: 0.058, chasm: 0.35 },
    { name: "highlands", hills: 0.65, tiers: 3, rise: 1.20, bench: 0.26, scale: 0.050, chasm: 0.30 },
    { name: "mesa", hills: 0.35, tiers: 2, rise: 2.00, bench: 0.15, scale: 0.050, chasm: 0.45 },
    { name: "gorge", hills: 0.52, tiers: 3, rise: 1.35, bench: 0.20, scale: 0.048, chasm: 0.80 },
  ];
  readonly profile: (typeof Terrain.Profiles)[number];
  private readonly px: number;
  private readonly pz: number;

  constructor(readonly grid: Grid, seed: number, readonly relief = 1, level = 1, authored: LevelTerrain | null = null) {
    this.bridges = new BridgeSpans(grid, (c) => this.isPit(c) || this.isLava(c), (x, z) => this.hillAt(x, z));
    const rnd = mulberry(seed);
    this.ox = rnd() * 100; this.oz = rnd() * 100;
    this.px = rnd() * 100; this.pz = rnd() * 100;
    this.scale = 0.17 + rnd() * 0.05;
    const named = authored ? Terrain.Profiles.find((p) => p.name === authored.profile) : undefined;
    this.profile = named ?? Terrain.Profiles[(Math.max(1, level) - 1) % Terrain.Profiles.length];
    const b = grid.base;
    this.padX = b.x; this.padZ = b.y;
    if (relief <= 0) return;
    this.open(authored);
    this.levelGorges();
  }

  /**
   * Ground within this many cells of a gorge is levelled to one height, fading back to the natural relief over
   * `GorgeBlend` more. A gorge dropped onto the rolling benches used to follow them: where it lay along a bench edge
   * the high side was a cliff top and the low side barely a step, so the chasm read as a cliff, and a bridge from the
   * top bank hung in the air over the bottom one (Kevin, 2026-09-21). Level ground all round makes every gorge a
   * trench with a wall on each side, and every bridge meets a bank at deck height at both ends.
   */
  static readonly GorgeFlat = 1.2;
  static readonly GorgeBlend = 2.2;
  private readonly gorges: { cells: Cell[]; level: number; minX: number; maxX: number; minZ: number; maxZ: number }[] = [];

  private levelGorges(): void {
    const hole = (i: number) => this.pits.has(i) || this.lava.has(i);
    const seen = new Set<number>();
    for (const start of [...this.pits, ...this.lava]) {
      if (seen.has(start)) continue;
      const cells: Cell[] = [];
      const stack = [start];
      seen.add(start);
      while (stack.length > 0) {
        const c = this.grid.cellAt(stack.pop()!);
        cells.push(c);
        for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const n = new Cell(c.x + dx, c.y + dz);
          if (!this.grid.inBounds(n)) continue;
          const i = this.grid.index(n);
          if (hole(i) && !seen.has(i)) { seen.add(i); stack.push(i); }
        }
      }
      // the banks' natural height: the cluster and its rim, sampled before any levelling
      let sum = 0, n = 0;
      for (const c of cells) for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) { sum += this.naturalAt(c.x + dx, c.y + dz); n++; }
      const reach = Terrain.GorgeFlat + Terrain.GorgeBlend + 0.5;
      this.gorges.push({
        cells, level: sum / n,
        minX: Math.min(...cells.map((c) => c.x)) - reach, maxX: Math.max(...cells.map((c) => c.x)) + reach,
        minZ: Math.min(...cells.map((c) => c.y)) - reach, maxZ: Math.max(...cells.map((c) => c.y)) + reach,
      });
    }
  }

  /**
   * Reads the level's chasms off the GRID.
   *
   * This used to roll its own: a die per blocked cell, then a flood fill over blocked clumps. Both were wrong for the
   * same reason — a chasm the simulation does not know about is a chasm the road can never cross, so the bridge was
   * unreachable and `spans` was always empty. Chasms are level data now (`LevelData.chasm`, `CellKind.Chasm`) and
   * this only has to decide which of them are molten.
   */
  private open(authored: LevelTerrain | null): void {
    const molten = new Set((authored?.lava ?? []).map((t) => `${t.x}|${t.y}`));
    for (let i = 0; i < this.grid.cellCount; i++) {
      const c = this.grid.cellAt(i);
      if (this.grid.kindAt(c) !== CellKind.Chasm) continue;
      (molten.has(`${c.x}|${c.y}`) ? this.lava : this.pits).add(i);
    }
  }

  isPit(c: Cell): boolean { return this.grid.inBounds(c) && this.pits.has(this.grid.index(c)); }
  isLava(c: Cell): boolean { return this.grid.inBounds(c) && this.lava.has(this.grid.index(c)); }
  /** True where there is ground to stand on: not a chasm, not a lava pool, on the map. */
  solid(c: Cell): boolean { return this.grid.inBounds(c) && !this.isPit(c) && !this.isLava(c); }

  /** Re-reads the road so bridges follow the draft (BridgeSpans.setRoad). */
  setRoad(road: RoadMap): void { this.bridges.setRoad(road); }
  /** The bridge spans on the current road: one per run of chasm cells, with the heading it runs along. */
  get spans(): readonly BridgeSpan[] { return this.bridges.spans; }
  /** The decks as fitted to the drawn gorge (the road view fits them once the ground exists; bridgeFit.ts). */
  setFits(fits: readonly (BridgeFit | null)[]): void { this.bridges.setFits(fits); }
  /** Height of a fitted bridge deck covering a world point (its seats on the banks included), else NaN. */
  deckOver(x: number, z: number): number { return this.bridges.deckOver(x, z); }
  /** The level deck guess over a bridged cell, or null where the road does not bridge it. */
  bridgeAt(c: Cell): number | null { return this.bridges.levelAt(this.grid.index(c)) ?? null; }

  /**
   * World y of the rolling ground at a world point. Three octaves of smooth value noise, flattened to a pad under the
   * fortress (the base model wants level footings) and eased back in over about three cells.
   */
  hillAt(x: number, z: number): number {
    let h = this.naturalAt(x, z);
    for (const g of this.gorges) {
      if (x < g.minX || x > g.maxX || z < g.minZ || z > g.maxZ) continue;
      let d = Infinity;
      for (const c of g.cells) d = Math.min(d, Math.hypot(Math.max(0, Math.abs(x - c.x) - 0.5), Math.max(0, Math.abs(z - c.y) - 0.5)));
      const w = d <= Terrain.GorgeFlat ? 1 : 1 - smoothstep(Math.min(1, (d - Terrain.GorgeFlat) / Terrain.GorgeBlend));
      h += (g.level - h) * w;
    }
    return h;
  }

  /** The relief before gorges are levelled into it (see `levelGorges`). */
  private naturalAt(x: number, z: number): number {
    if (this.relief <= 0) return 0;
    const s = this.scale;
    const n = noise2(x * s + this.ox, z * s + this.oz) * 0.58
      + noise2(x * s * 2.1 + this.oz, z * s * 2.1 + this.ox) * 0.28
      + noise2(x * s * 4.3 + 11, z * s * 4.3 + 7) * 0.14;
    const rolling = (n - 0.5) * this.profile.hills;
    const pad = Math.min(1, Math.max(0, (Math.hypot(x - this.padX, z - this.padZ) - 2.2) / 3.4));
    return (rolling + this.plateauAt(x, z)) * smoothstep(pad);
  }

  /**
   * The stepped part of the land: broad flat benches at different heights, joined by ramps.
   *
   * Two things stop this becoming the wedding cake again. The field is sampled through a domain warp, so a bench's
   * outline is a wandering curve that owes nothing to the cell grid or to the noise lattice; and the step between
   * benches is a smoothstep across `bench` of a tier's width rather than a vertical wall, so what the player walks up
   * is a slope. What survives is the thing the reference has and the rolling meadow did not: ground that is flat here
   * and flat there and a metre apart in height.
   */
  plateauAt(x: number, z: number): number {
    const P = this.profile;
    if (P.tiers <= 0) return 0;
    const t = this.tierAt(x, z);
    const k = Math.floor(t), f = t - k;
    // How fast the tier coordinate changes here, per world unit. Where the domain warp squeezes the field, a ramp
    // that is `bench` of a tier wide in noise space can be a tenth of a unit wide on the ground: a two-unit rise in
    // less than one quad of the ground mesh. The mesh cannot draw that — it steps the ramp from vertex to vertex, and
    // everything painted on the slope shows the steps as a sawtooth (Kevin, 2026-09-21). So a ramp never gets
    // narrower than `MinRamp` world units: where the field is squeezed, it spends more of the tier on the ramp.
    const e = Terrain.RampProbe;
    const grad = Math.hypot(this.tierAt(x + e, z) - this.tierAt(x - e, z), this.tierAt(x, z + e) - this.tierAt(x, z - e)) / (2 * e);
    const bench = Math.min(Terrain.MaxRampShare, Math.max(P.bench, Terrain.MinRamp * grad));
    const ramp = f < bench ? smoothstep(f / bench) : 1;
    return (k + ramp - (P.tiers + 1) / 2) * P.rise;
  }

  /** Narrowest a ramp between benches may be, in world units: four quads of the ground mesh. */
  static readonly MinRamp = 1.0;
  /** Most of a tier a squeezed ramp may take, so a bench always keeps some flat top. */
  static readonly MaxRampShare = 0.85;
  /** Half-width of the central difference that measures how squeezed the tier field is, in world units. */
  static readonly RampProbe = 0.1;

  /** Which tier a point is on, as a continuous coordinate: the integer part is the bench, the fraction how far across. */
  private tierAt(x: number, z: number): number {
    const P = this.profile;
    const s = P.scale;
    // The warp has to scale with the feature size. It was a flat 11 units, which is most of a period at s = 0.075 but
    // only half of one at s = 0.05 — and the moment the warp stops being large relative to a bench, the terrace edges
    // straighten out and the level goes back to parallel bands marching across the map. Keep it at ~0.8 of a period.
    const amp = 0.8 / s;
    const wx = x + (noise2(x * s * 1.9 + 31 + this.px, z * s * 1.9 + 17) - 0.5) * amp;
    const wz = z + (noise2(x * s * 1.9 + 57, z * s * 1.9 + 91 + this.pz) - 0.5) * amp;
    const raw = noise2(wx * s + this.px, wz * s + this.pz) * 0.68 + noise2(wx * s * 2.4 + 5, wz * s * 2.4 + 3) * 0.32;
    // value noise sits around the middle of its range, so without a stretch the top and bottom benches are never
    // reached and a three-tier level reads as a one-tier one with a couple of dents in it
    const n = Math.min(0.9999, Math.max(0, (raw - 0.24) / 0.52));
    return n * (P.tiers + 1);
  }

  /** World y of the ground at a world point: the hills, or a bridge deck where one spans a chasm. */
  heightAt(x: number, z: number): number {
    const i = this.cellIndex(x, z);
    if (i < 0) return this.hillAt(x, z);
    const b = this.bridges.deckAt(i, x, z);
    if (b !== undefined) return b;
    if (this.pits.has(i)) return this.floorAt(x, z);
    return this.hillAt(x, z);
  }

  /**
   * The grid index (Grid.index) of the cell a world point rounds to, or −1 off the grid — without making a Cell, since
   * the height lookups run per unit per frame.
   */
  private cellIndex(x: number, z: number): number {
    const cx = Math.round(x), cz = Math.round(z), g = this.grid;
    return cx < 0 || cz < 0 || cx >= g.width || cz >= g.height ? -1 : cz * g.width + cx;
  }

  /**
   * World y of road level at a point: a bridge deck where one spans a chasm, and over an open chasm or lava pool the
   * levelled bank height a bridge would be laid at — never the floor below. What the flat overlays drawn at road
   * level (the road card's preview edges and routes, tile markers, fit badges, aura rings) sit on: sampled with
   * `heightAt`, a vertex that rounded into an unbridged chasm cell dropped to its floor, and a line drawn without a
   * depth test hung down the canyon wall (Kevin, 2026-09-30).
   */
  roadLevelAt(x: number, z: number): number {
    const i = this.cellIndex(x, z);
    return (i < 0 ? undefined : this.bridges.deckAt(i, x, z)) ?? this.hillAt(x, z);
  }

  /** World y of a chasm floor / a molten surface under a point: the land there, dropped by a fixed depth. */
  floorAt(x: number, z: number): number { return this.hillAt(x, z) - Terrain.PitDepth; }
  lavaAt(x: number, z: number): number { return this.hillAt(x, z) - Terrain.LavaDepth; }

  /** World y at a cell's centre, ignoring any bridge over it — the height the ground itself would have. */
  groundOf(c: Cell): number { return this.hillAt(c.x, c.y); }
}

/** World y of the ground under a point, on the current level's relief (0 when there is none). */
export function groundAt(x: number, z: number): number { return Terrain.current ? Terrain.current.heightAt(x, z) : 0; }

/** World y of road level at a point (`Terrain.roadLevelAt`): the ground, a bridge deck, or a chasm's bank height. */
export function roadLevelAt(x: number, z: number): number { return Terrain.current ? Terrain.current.roadLevelAt(x, z) : 0; }
